import { PublicReview } from '@shared/types';
import { PublicEducator } from '@cademy-package/sdk-component-types';
import classNames from 'classnames';
import Link from 'next/link';
import { FunctionComponent } from 'react';
import styles from './styles.module.scss';
import { useCourseLink } from '../../../hooks/useCourseLink';
import { PublicEducatorCourseReview } from '@shared/types';

export type CourseReviewLinkProps = {
    className?: string;
    review: PublicReview;
    educator: PublicEducator;
};

const DefaultItemReviewedMeta: FunctionComponent<{ educator: PublicEducator }> = ({ educator }) => {
    return (
        <span
            itemProp="itemReviewed"
            itemScope
            itemType="https://schema.org/EducationalOrganization"
        >
            <meta itemProp="name" content={educator.name} />
            <meta itemProp="url" content={`/${educator.slug}`} />
        </span>
    );
};

type CourseReviewedMetaProps = {
    className?: string;
    educator: PublicEducator;
    course: PublicEducatorCourseReview['course'];
};

const CourseReviewedMeta: FunctionComponent<CourseReviewedMetaProps> = ({
    className,
    course,
    educator,
}) => {
    const courseLink = useCourseLink(educator.slug, course.slug);

    return (
        <div
            className={classNames(styles.courseLink, className)}
            itemProp="itemReviewed"
            itemScope
            itemType="https://schema.org/Course"
        >
            Reviewed{' '}
            <Link {...courseLink} title={course.name} itemProp="url">
                <span itemProp="name">{course.name}</span>
            </Link>
        </div>
    );
};

const CourseReviewLink: FunctionComponent<CourseReviewLinkProps> = ({
    className,
    review,
    educator,
}) => {
    if (review.entity !== 'course') {
        return <DefaultItemReviewedMeta educator={educator} />;
    }
    if (!('course' in review)) {
        return <DefaultItemReviewedMeta educator={educator} />;
    }
    if (review.course.published !== true) {
        return <DefaultItemReviewedMeta educator={educator} />;
    }

    return <CourseReviewedMeta educator={educator} className={className} course={review.course} />;
};

export default CourseReviewLink;
